import { type KyInstance } from 'ky';

import { buildSearchParams, handleRequest } from '@/utils/httpUtils';

import type {
  AddressType,
  CreateSavedAddressReq,
  DeleteSavedAddressResponse,
  PingSavedAddressResponse,
  SavedAddressesResponse,
  SavedAddressResponse,
  UpdateSavedAddressReq,
} from '@/types/parcel/userData';

import {
  BalanceReq,
  BalanceRes,
  GetPluginDetailsReq,
  GetPluginsReq,
  InvoicesReq,
  MercadoPagoPaymentLinkReq,
  PaymentLinkResponse,
  ProfileReq,
  ProfileRes,
  RatesReq,
  RatesRes,
  StripePaymentLinkReq,
  StripePricesReq,
  StripePricesResponse,
  UpdatePasswordReq,
  UpdateProfileReq,
  UpdateProfileRes,
} from './types';

type OrganizationType = 'principal' | 'secondary';

interface Organization {
  organizationCode: string;
  name: string;
  type: OrganizationType;
}

interface Credentials {
  clientId: string;
  clientSecret: string;
  legacyApiKey: string;
}

interface SubAccount {
  userCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  active: boolean;
  organization: Organization;
  credentials: Credentials;
}

interface CreateSubAccountData {
  organizationCode: string;
  userName: string;
  password: string;
  user: {
    firstName: string;
    lastName: string;
    phone: string;
  };
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  code?: number;
  traceId?: string;
}

type OrganizationsResponse = ApiResponse<Organization[]>;
type SubAccountsResponse = ApiResponse<SubAccount[]>;
type CreateSubAccountResponse = ApiResponse<SubAccount>;

export interface IReqUserData {
  profile: ProfileReq;
  updateProfile: UpdateProfileReq;
  balance: BalanceReq;
  rates: RatesReq;
  invoices: InvoicesReq;
  updatePassword: UpdatePasswordReq;
  mercadoPagoPaymentLink: MercadoPagoPaymentLinkReq;
  stripePaymentLink: StripePaymentLinkReq;
  stripePrices: StripePricesReq;
  getOrganizations: () => Promise<OrganizationsResponse>;
  getSubAccounts: () => Promise<SubAccountsResponse>;
  registerSubAccount: (data: CreateSubAccountData) => Promise<CreateSubAccountResponse>;
  getPlugins: GetPluginsReq;
  getPluginDetails: GetPluginDetailsReq;
  getSavedAddresses: (type?: AddressType) => Promise<SavedAddressesResponse>;
  createSavedAddress: (data: CreateSavedAddressReq) => Promise<SavedAddressResponse>;
  updateSavedAddress: (
    addressId: string,
    data: UpdateSavedAddressReq,
  ) => Promise<SavedAddressResponse>;
  pingSavedAddress: (addressId: string) => Promise<PingSavedAddressResponse>;
  deleteSavedAddress: (addressId: string) => Promise<DeleteSavedAddressResponse>;
}

export function reqUserData(http: KyInstance): IReqUserData {
  const route = 'api/user';

  return {
    profile: async () => handleRequest<ProfileRes>(http, 'get', `${route}/profile`),
    updateProfile: async (ars) =>
      handleRequest<UpdateProfileRes>(http, 'patch', `${route}/profile`, { json: ars }),
    balance: async () => handleRequest<BalanceRes>(http, 'get', `${route}/balance`),
    rates: async () => handleRequest<RatesRes>(http, 'get', `${route}/rates`),
    invoices: async (ars) => {
      const { startDate, endDate, page, pageSize } = ars || {};

      let queryParams = {};

      if (startDate && endDate) {
        queryParams = {
          ...queryParams,
          startDate,
          endDate,
        };
      }

      if (page) {
        queryParams = {
          ...queryParams,
          page,
        };
      }

      if (pageSize) {
        queryParams = {
          ...queryParams,
          pageSize,
        };
      }

      return handleRequest(http, 'get', `${route}/invoices`, {
        searchParams: buildSearchParams(queryParams),
      });
    },
    updatePassword: async (ars) => {
      try {
        return await handleRequest(http, 'patch', `${route}/update-password`, { json: ars });
      } catch (error) {
        const { createUserFriendlyError } = await import('@/utils/httpUtils');

        let customMessage = '';
        if (error && typeof error === 'object' && 'response' in error) {
          const httpError = error as any;
          if (httpError.response?.status === 400) {
            customMessage = 'La contraseña actual es incorrecta';
          } else if (httpError.response?.status === 422) {
            customMessage = 'La nueva contraseña no cumple con los requisitos de seguridad';
          }
        }

        throw await createUserFriendlyError(error, customMessage);
      }
    },

    mercadoPagoPaymentLink: async (ars) =>
      handleRequest<PaymentLinkResponse>(
        http,
        'post',
        `${route}/balance/mercado-pago/payment-link`,
        { json: ars },
      ),

    stripePaymentLink: async (ars) =>
      handleRequest<PaymentLinkResponse>(http, 'post', `${route}/balance/stripe/payment-link`, {
        json: ars,
      }),

    stripePrices: async () =>
      handleRequest<StripePricesResponse>(http, 'get', `${route}/balance/stripe/prices`),

    getOrganizations: async () =>
      handleRequest<OrganizationsResponse>(http, 'get', `${route}/sub-accounts/organizations`),
    getSubAccounts: async () =>
      handleRequest<SubAccountsResponse>(http, 'get', `${route}/sub-accounts`),
    registerSubAccount: async (data) => {
      try {
        return await handleRequest<CreateSubAccountResponse>(
          http,
          'post',
          `${route}/sub-accounts`,
          { json: data },
        );
      } catch (error) {
        const { createUserFriendlyError } = await import('@/utils/httpUtils');

        let customMessage = '';
        if (error && typeof error === 'object' && 'response' in error) {
          const httpError = error as any;
          if (httpError.response?.status === 400) {
            customMessage =
              'Los datos del usuario no son válidos. Verifica que el nombre de usuario no esté en uso y que todos los campos estén completos.';
          } else if (httpError.response?.status === 409) {
            customMessage =
              'Ya existe un usuario con este nombre de usuario o teléfono. Intenta con datos diferentes.';
          } else if (httpError.response?.status === 422) {
            customMessage =
              'Los datos proporcionados no son válidos. Verifica el formato del teléfono y la contraseña.';
          }
        }

        throw await createUserFriendlyError(error, customMessage);
      }
    },
    getPlugins: async () => handleRequest(http, 'get', `${route}/plugins`),
    getPluginDetails: async ({ id, clientId }) =>
      handleRequest(http, 'get', `${route}/plugins/${id}/details`, {
        headers: {
          'X-Client-ID': clientId,
        },
      }),

    getSavedAddresses: async (type) => {
      const searchParams = type ? { type } : {};
      return handleRequest<SavedAddressesResponse>(http, 'get', `${route}/addresses`, {
        searchParams: buildSearchParams(searchParams),
      });
    },

    createSavedAddress: async (data) =>
      handleRequest<SavedAddressResponse>(http, 'post', `${route}/addresses`, {
        json: data,
      }),

    updateSavedAddress: async (addressId, data) =>
      handleRequest<SavedAddressResponse>(http, 'put', `${route}/addresses/${addressId}`, {
        json: data,
      }),

    pingSavedAddress: async (addressId) =>
      handleRequest<PingSavedAddressResponse>(
        http,
        'patch',
        `${route}/addresses/${addressId}/ping`,
      ),

    deleteSavedAddress: async (addressId) =>
      handleRequest<DeleteSavedAddressResponse>(http, 'delete', `${route}/addresses/${addressId}`),
  };
}
